import {
  attach,
  combine,
  createDomain,
  createEffect,
  createEvent,
  sample,
} from "effector";
import { createGate } from "effector-react";
import { articlesApi } from "../api";
import { ArticleDetails } from "../types";
import { countEqualCharacters } from "../../../shared/libs/count-equal-characters";
import { extractDomain } from "../../../shared/libs/extract-domain";
import { format, parseISO } from "date-fns";
import { notifications } from "@mantine/notifications";
import { viewerEntity } from "../../../shared/entities/viewer";

const Gate = createGate<{
  articleId: string;
}>();

const domain = createDomain();

const fetchDataFx = createEffect(articlesApi.getById);
const deleteSimilarArticleByIdFx = createEffect(
  articlesApi.deleteSimilarArticleById
);

const deleteSimilarArticleById = createEvent<string>();

const getDetails = createEvent<{ articleId: string }>();

const $data = domain.createStore<ArticleDetails | null>(null);

const $errors = $data.map((data) => {
  if (!data?.errors) return [];

  return data.errors.map((error) => {
    const regex = /(.*?)\s*\[(.*?)\]\s*(.*)/;
    const match = regex.exec(error.message);

    if (match) {
      return {
        id: error.id,
        title: match[2],
        message: match[3] || match[1],
      };
    }

    return {
      id: error.id,
      title: "Unknown",
      message: error.message,
    };
  });
});

sample({
  clock: viewerEntity.$currentSimilarity,
  source: {
    gateState: Gate.state,
  },
  filter: ({ gateState }) => Boolean(gateState?.articleId),
  fn: ({ gateState }) => ({ articleId: gateState?.articleId }),
  target: getDetails,
});

sample({
  clock: [Gate.open, getDetails],
  fn: (params) => params.articleId,
  target: fetchDataFx,
});

const $isFetched = domain
  .createStore(false)
  .on(fetchDataFx.finally, () => true);

const $isLoading = combine(
  {
    isFetched: $isFetched,
    pending: fetchDataFx.pending,
  },
  ({ isFetched, pending }) => !isFetched && pending
);

sample({
  clock: fetchDataFx.doneData,
  fn: (data) => ({
    ...data,
    article: {
      ...data.article,
      createdAt: format(parseISO(data.article.createdAt), "dd/MM/yyyy HH:mm"),
      date: data.article.date
        ? format(parseISO(data.article.date), "dd/MM/yyyy HH:mm")
        : undefined,
    },
    similarities: data.similarities.map((row) => ({
      ...row,
      date: row.date
        ? format(parseISO(row.date), "dd/MM/yyyy HH:mm")
        : undefined,
      domain: extractDomain(row.url),
      count: data.similarities.filter(s => extractDomain(s.url) == extractDomain(row.url)).length,
    })),
  }),
  target: $data,
});

const downloadScreenshotsFx = attach({
  source: $data,
  effect: (data) => {
    if (!data) throw new Error("No data to export");

    return articlesApi.downloadScreenshots(data.article.id);
  },
});

const exportToCsvFx = attach({
  source: $data,
  effect: (data) => {
    if (!data?.similarities) throw new Error("No data to export");

    const headers = [
      "Domain",
      "URL",
      "Title",
      "Snippet",
      "Date",
      "Search Engine",
      "Screenshot URL",
    ];

    const similaritiesMap = new Map<string, (typeof data.similarities)[number]>();
    for (const s of data.similarities) {
      const domain = extractDomain(s.url);
      if (!similaritiesMap.has(domain) ||
        countEqualCharacters(data.article.title, s.title) > countEqualCharacters(data.article.title, similaritiesMap.get(domain)?.title ?? '')) {
        similaritiesMap.set(domain, s);
      }
    }

    const rows = Array.from(similaritiesMap.values()).map((item) => [
      extractDomain(item.url),
      item.url,
      item.title,
      item.snippet,
      item.date ?? "-",
      item.searchEngine,
      `${window.location.origin}//images/screenshots/${item.screenshotId}.png`,
    ]);

    const stats = [
      [data.article.title, data.article.originalUrl],
      [`Total unique links count: ${similaritiesMap.size}`],
    ];

    const csvContent = [...stats, headers, ...rows]
      .map((row) =>
        row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "article_results.csv");

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
});

sample({
  clock: deleteSimilarArticleById,
  target: deleteSimilarArticleByIdFx,
});

sample({
  clock: deleteSimilarArticleByIdFx.doneData,
  filter: (data) => Boolean(data.articleId),
  fn: (data) => {
    notifications.show({
      title: "Similar article deleted",
      message: "Similar article deleted successfully",
      color: "green",
    });
    return { articleId: data.articleId };
  },
  target: getDetails,
});

domain.onCreateStore((store) => store.reset(Gate.close));

export const articleDetailsEntityModel = {
  $data,
  $errors,
  Gate,
  exportToCsvFx,
  downloadScreenshotsFx,
  deleteSimilarArticleById,
  $isLoading,
};
